import { Box, Typography, Grid, Chip, Tooltip, IconButton } from '@mui/material';
import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import StarIcon from '@mui/icons-material/Star';
import { BaseCard } from '../common/BaseCard';
import { formatCep, formatPhone } from '../utils/formatters';
import { ILocalizacaoPF, ILocalizacaoPJ, IEndereco, ITelefone, IEmail } from '../../../../../../application/models/dossie';

export const LocalizacaoContatosCard = ({
  localizacao,
}: {
  localizacao: ILocalizacaoPF | ILocalizacaoPJ | null;
}) => {
  if (!localizacao) return null;

  const enderecos: IEndereco[] = localizacao.enderecos || [];
  const emails: IEmail[] = localizacao.emails || [];
  const telefones: ITelefone[] = localizacao.telefones || [];

  const getOperadoraStyle = (operadora: string) => {
    const op = operadora.toUpperCase();
    if (op.includes('CLARO')) return { bg: '#ffebee', txt: '#c62828' };
    if (op.includes('VIVO')) return { bg: '#f3e5f5', txt: '#6a1b9a' };
    if (op.includes('TIM')) return { bg: '#e3f2fd', txt: '#1565c0' };
    if (op.includes('OI')) return { bg: '#fff8e1', txt: '#f57f17' };
    if (op.includes('ALGAR')) return { bg: '#f1f8e9', txt: '#33691e' };
    return { bg: '#f0f0f0', txt: '#555' };
  };

  return (
    <BaseCard
      title="Localização & Contatos"
      icon={<RoomOutlinedIcon />}
      canSplit={enderecos.length > 2 || telefones.length > 8}
    >
      <Grid container spacing={4} className="localizacao-grid-container">
        {/* Coluna 1: Endereços e Emails */}
        <Grid item xs={12} lg={5} className="coluna-enderecos">
          <Typography
            variant="caption"
            sx={{ color: '#1e293b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 2, display: 'block' }}
          >
            Endereços
          </Typography>
          <Box display="flex" flexDirection="column" gap={2} mb={3.5}>
            {enderecos.length > 0 ? (
              enderecos.map((endereco: IEndereco, idx: number) => (
                <Box
                  key={idx}
                  className="endereco-card-item avoid-break"
                  sx={{
                    p: 2,
                    borderRadius: '12px',
                    border: '1px solid #ece7f2',
                    backgroundColor: '#faf8fc',
                  }}
                >
                  <Typography variant="body1" sx={{ color: '#1e293b', fontWeight: 700, mb: 0.5 }}>
                    {endereco.Logradouro}, {endereco.Numero}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b', mb: 0.5 }}>
                    {endereco.Complemento && `${endereco.Complemento} - `}
                    {endereco.Bairro}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b', mb: 1.5 }}>
                    {endereco.Cidade} / {endereco.UF}
                  </Typography>
                  <Chip
                    label={`CEP: ${formatCep(endereco.CEP)}`}
                    size="small"
                    sx={{ backgroundColor: '#f3eff7', color: '#68457c', fontWeight: 700, borderRadius: '6px' }}
                  />
                </Box>
              ))
            ) : (
              <Typography variant="body2" sx={{ color: '#888' }}>Nenhum endereço encontrado.</Typography>
            )}
          </Box>

          <Typography
            variant="caption"
            sx={{ color: '#1e293b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1.5, display: 'block' }}
          >
            E-mail
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {emails.length > 0 ? (
              emails.map((emailItem: IEmail, idx: number) => (
                <Chip
                  key={idx}
                  label={emailItem.Email}
                  variant="outlined"
                  sx={{
                    borderColor: '#e2d9ec',
                    color: '#475569',
                    fontWeight: 600,
                    backgroundColor: '#faf8fc',
                    borderRadius: '8px',
                  }}
                />
              ))
            ) : (
              <Typography variant="body2" sx={{ color: '#888' }}>Nenhum e-mail encontrado.</Typography>
            )}
          </Box>
        </Grid>

        {/* Coluna 2: Telefones */}
        <Grid item xs={12} lg={7} className="coluna-telefones">
          <Typography
            variant="caption"
            sx={{ color: '#1e293b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 2, display: 'block' }}
          >
            Telefones ({telefones.length})
          </Typography>
          <Box
            className="telefones-grid-container"
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(260px, 1fr))' },
              gap: 1.5,
              mb: 4,
            }}
          >
            {telefones.length > 0 ? (
              [...telefones]
                .map((tel: ITelefone, idx: number) => {
                  const styleOp = tel.Operadora ? getOperadoraStyle(tel.Operadora) : null;
                  const isWhatsApp = Boolean(
                    tel.WhatsApp === true ||
                    String(tel.WhatsApp).toLowerCase() === 'true' ||
                    String(tel.WhatsApp) === '1'
                  );
                  const isPremium = Boolean(
                    tel.Premium === true ||
                    String(tel.Premium).toLowerCase() === 'true' ||
                    String(tel.Premium) === '1' ||
                    isWhatsApp
                  );
                  return (
                    <Box
                      key={idx}
                      className="telefone-card-item avoid-break"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 1.25,
                        px: 1.75,
                        backgroundColor: '#fbfafc',
                        borderRadius: '10px',
                        border: '1px solid #ece7f2',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: '#723989',
                          backgroundColor: '#ffffff',
                          boxShadow: '0 2px 8px rgba(114, 57, 137, 0.08)',
                        },
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <Typography variant="body2" sx={{ color: '#1e293b', fontWeight: 700, fontSize: '0.9rem' }}>
                          {formatPhone(tel.DDD, tel.Telefone)}
                        </Typography>

                        {tel.Operadora && styleOp && (
                          <Chip
                            className="no-print"
                            label={tel.Operadora}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.65rem',
                              backgroundColor: styleOp.bg,
                              color: styleOp.txt,
                              fontWeight: 700,
                              borderRadius: '4px',
                            }}
                          />
                        )}

                        {isPremium && (
                          <Chip
                            className="no-print"
                            icon={<StarIcon sx={{ fontSize: '11px !important', color: '#b45309 !important' }} />}
                            label="PREMIUM"
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.65rem',
                              backgroundColor: '#fef3c7',
                              color: '#92400e',
                              border: '1px solid #fde68a',
                              fontWeight: 700,
                              borderRadius: '4px',
                              '& .MuiChip-icon': {
                                marginLeft: '4px',
                                marginRight: '-4px',
                              },
                            }}
                          />
                        )}

                      </Box>

                      {isWhatsApp && (
                        <Tooltip title="Abrir no WhatsApp" arrow placement="top">
                          <IconButton
                            className="no-print"
                            size="small"
                            onClick={() =>
                              window.open(
                                `https://api.whatsapp.com/send/?phone=55${tel.DDD}${tel.Telefone}&text=Ol%C3%A1%2C+tudo+bem%3F&type=phone_number&app_absent=0`,
                                '_blank'
                              )
                            }
                            sx={{
                              backgroundColor: '#e8f5e9',
                              color: '#2e7d32',
                              width: 28,
                              height: 28,
                              '&:hover': { backgroundColor: '#c8e6c9' },
                            }}
                          >
                            <WhatsAppIcon sx={{ fontSize: '1.05rem' }} />
                          </IconButton>
                        </Tooltip>
                      )}

                    </Box>
                  );
                })
            ) : (
              <Typography variant="body2" sx={{ color: '#888' }}>Nenhum telefone encontrado.</Typography>
            )}
          </Box>
        </Grid>
      </Grid>
    </BaseCard>
  );
};
